import React, { memo, useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';

const ZOOM = 2.5;

/**
 * Amazon-style image zoom.
 *
 * Desktop (≥992px):
 *   Hover → shows semi-transparent crosshair on image.
 *   A zoomed panel renders via React portal, absolutely positioned
 *   to the right of the image element (scrollY-aware).
 *
 * Mobile (<992px):
 *   Touch-drag → circular lens magnifier overlaid on image.
 */
function Magnifier({ imgSrc, imgWidth, imgHeight, magnifierRadius = 120 }) {
  const imgRef = useRef(null);

  const [hover, setHover]         = useState(false);
  const [cursor, setCursor]       = useState({ x: 0, y: 0, xPct: 0, yPct: 0, imgW: 0, imgH: 0 });
  const [panelPos, setPanelPos]   = useState({ top: 0, left: 0, width: 0, height: 0 });

  const [touchLens, setTouchLens] = useState({ visible: false, top: 0, left: 0, xPct: 0, yPct: 0 });

  const getImgRect = () => imgRef.current?.getBoundingClientRect();

  // ── Update panel position (right of image, page-absolute) ────────────────
  const refreshPanelPos = useCallback(() => {
    const r = getImgRect();
    if (!r) return;
    setPanelPos({
      top:    r.top  + window.scrollY,
      left:   r.right + window.scrollX + 14,
      width:  r.width,
      height: r.height,
    });
  }, []);

  // ── Desktop handlers ──────────────────────────────────────────────────────
  const onMouseEnter = () => {
    if (window.innerWidth < 992) return;
    refreshPanelPos();
    setHover(true);
  };
  const onMouseLeave = () => setHover(false);
  const onMouseMove  = (e) => {
    if (window.innerWidth < 992) return;
    const r = getImgRect();
    if (!r) return;
    const x = Math.max(0, Math.min(e.clientX - r.left, r.width));
    const y = Math.max(0, Math.min(e.clientY - r.top,  r.height));
    setCursor({ x, y, xPct: x / r.width, yPct: y / r.height, imgW: r.width, imgH: r.height });
  };

  useEffect(() => {
    if (!hover) return;
    const handler = () => refreshPanelPos();
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, [hover, refreshPanelPos]);

  // ── Touch handlers ────────────────────────────────────────────────────────
  const calcTouch = (touch) => {
    const r = getImgRect();
    if (!r) return;
    const x = Math.max(0, Math.min(touch.clientX - r.left, r.width));
    const y = Math.max(0, Math.min(touch.clientY - r.top,  r.height));
    setTouchLens({ visible: true, top: y - magnifierRadius, left: x - magnifierRadius, xPct: x / r.width, yPct: y / r.height });
  };
  const onTouchStart = (e) => calcTouch(e.touches[0]);
  const onTouchMove  = (e) => { e.preventDefault(); calcTouch(e.touches[0]); };
  const onTouchEnd   = () => setTouchLens((p) => ({ ...p, visible: false }));

  // ── Crosshair ─────────────────────────────────────────────────────────────
  const chW = cursor.imgW / ZOOM;
  const chH = cursor.imgH / ZOOM;
  const chL = cursor.x - chW / 2;
  const chT = cursor.y - chH / 2;

  // ── Zoom panel img offset ─────────────────────────────────────────────────
  // Image is ZOOM× bigger than panel. We shift it so the hovered spot is centered.
  const zoomImgTop  = -(cursor.yPct * cursor.imgH * ZOOM - panelPos.height / 2);
  const zoomImgLeft = -(cursor.xPct * cursor.imgW * ZOOM - panelPos.width  / 2);

  // ── Lens zoom offset ──────────────────────────────────────────────────────
  const lensD       = magnifierRadius * 2;
  const lensImgTop  = -(touchLens.yPct * (lensD * ZOOM) - magnifierRadius);
  const lensImgLeft = -(touchLens.xPct * (lensD * ZOOM) - magnifierRadius);

  return (
    <div style={{ position: 'relative', display: 'block' }}>
      {/* ── Main image ── */}
      <img
        ref={imgRef}
        src={imgSrc}
        alt="product"
        width={imgWidth}
        height={imgHeight}
        style={{
          maxHeight: '70vh', maxWidth: '100%',
          width: '100%', height: 'auto',
          display: 'block', userSelect: 'none', touchAction: 'none',
          cursor: hover ? 'crosshair' : 'default',
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      />

      {/* ── Crosshair box (desktop) ── */}
      {hover && cursor.imgW > 0 && (
        <div style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 10,
          width: chW, height: chH, top: chT, left: chL,
          background: 'rgba(110,54,201,0.10)',
          border: '1.5px solid rgba(110,54,201,0.5)',
          boxSizing: 'border-box',
        }} />
      )}

      {/* ── Touch lens (mobile) ── */}
      {touchLens.visible && (
        <div style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 20,
          width: lensD, height: lensD, borderRadius: '50%',
          top: touchLens.top, left: touchLens.left,
          border: '2px solid var(--gold-400)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
          overflow: 'hidden', background: '#fff',
        }}>
          <img
            src={imgSrc}
            alt="lens"
            style={{
              position: 'absolute', maxWidth: 'none',
              width: lensD * ZOOM, height: lensD * ZOOM,
              top: lensImgTop, left: lensImgLeft,
              objectFit: 'contain',
            }}
          />
        </div>
      )}

      {/* ── Portal zoom panel (desktop) ── */}
      {hover && ReactDOM.createPortal(
        <div style={{
          position: 'absolute',
          top:    panelPos.top,
          left:   panelPos.left,
          width:  panelPos.width,
          height: panelPos.height,
          overflow: 'hidden',
          border: '1.5px solid var(--ink-200)',
          borderRadius: 12,
          boxShadow: '0 8px 40px rgba(80,40,130,0.18)',
          background: '#fff',
          pointerEvents: 'none',
          zIndex: 9999,
        }}>
          <img
            src={imgSrc}
            alt="zoomed"
            style={{
              position: 'absolute', maxWidth: 'none',
              width:  cursor.imgW * ZOOM,
              height: cursor.imgH * ZOOM,
              top:    zoomImgTop,
              left:   zoomImgLeft,
              objectFit: 'contain',
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default memo(Magnifier);